import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LocationService, { Location } from "../../services/LocationService";
import TransporterService from "../../services/TransporterService";

export default function TransporterBaseStation() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [current, setCurrent] = useState<Location | null>(null);
  const [selection, setSelection] = useState("0");
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true); setLoaded(false); setError(""); setNotice("");
    const loadLocations = async () => {
      const items: Location[] = [];
      for (let skip = 0; ; skip += 500) {
        const response = await LocationService.getAllLocations({ label: "BASE_STATION", limit: 500, skip });
        const page = response.data.data;
        if (!Array.isArray(page?.content)) throw new Error("Could not load base stations");
        items.push(...page.content);
        if (items.length >= page.page.totalElements || !page.content.length) break;
      }
      return items.filter(location => location.isActive && location.label === "BASE_STATION");
    };
    Promise.all([TransporterService.getMyBaseStation(), loadLocations()])
      .then(([base, items]) => {
        if (!active) return;
        setCurrent(base); setSelection(String(base?.id || 0)); setLocations(items); setLoaded(true);
      })
      .catch(err => { if (active) setError(err?.response?.status === 405
        ? "Base station settings need the updated backend. The server does not yet support loading the saved base station."
        : err?.response?.data?.message || err.message || "Could not load base stations"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [reload]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true); setError(""); setNotice("");
    try {
      const base = await TransporterService.setMyBaseStation(Number(selection));
      setCurrent(base); setSelection(String(base?.id || 0));
      setNotice(base ? "Base station saved for all your trucks." : "Base station removed from all your trucks.");
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || "Could not save base station");
    } finally { setSaving(false); }
  };

  const unavailable = current && !locations.some(location => location.id === current.id);
  return <section className="bg-white rounded-lg p-5 my-5 border border-gray-200" aria-labelledby="base-station-title">
    <h2 id="base-station-title" className="text-lg font-semibold">Transporter base station</h2>
    <p className="text-sm text-gray-600 my-2">One base station applies to all your existing and future trucks. Changing it updates the whole fleet.</p>
    {loading ? <p role="status">Loading base stations…</p> : loaded ? <>
      <p className="text-sm my-2">Current base station: <strong>{current?.name || "Not assigned"}</strong></p>
      <form onSubmit={save} className="flex flex-wrap gap-3 items-end">
        <label className="flex flex-col gap-2 text-sm" htmlFor="transporter-base-station">Base station
          <select id="transporter-base-station" className="border rounded p-2" value={selection} disabled={saving}
            onChange={event => { setSelection(event.target.value); setNotice(""); }}>
            <option value="0">No base station</option>
            {unavailable && <option value={current.id} disabled>{current.name} (unavailable)</option>}
            {locations.map(location => <option key={location.id} value={location.id}>{location.name} — {location.address}</option>)}
          </select>
        </label>
        <button type="submit" className="primary_button" disabled={saving || selection === String(current?.id || 0)}>{saving ? "Saving…" : "Save base station"}</button>
      </form>
      {!locations.length && <p className="text-sm my-2">No active base stations found. Create a location with type Base station first.</p>}
    </> : null}
    <div className="flex gap-4 my-3 text-sm">
      <Link to="/locations" className="text-blue-700 underline">Manage locations</Link>
      <button type="button" className="text-blue-700 underline" disabled={loading || saving} onClick={() => setReload(value => value + 1)}>Refresh base stations</button>
    </div>
    {error && <p role="alert" className="text-red-700">{error}</p>}
    {notice && <p role="status" className="text-green-700">{notice}</p>}
  </section>;
}
